// Sprint 12.5 — mobile bottom tab bar (atlas shell). Fixed bottom, <768px only.
// Rendered by the pf-page screens AND (§S680) the idle home canvas, so every
// primary destination carries the same mobile IA.
//
// §S680 — destinations come from the shared nav registry (lib/nav-items.ts)
// and the ACTIVE tab is derived from location.hash here, NOT passed in by the
// host screen. The old `active` prop let Settings/Account/Guide/Admin pass ''
// which lit up Home while the user was elsewhere (platform audit); deriving
// from the hash makes at most ONE tab active, and none on non-tab routes.
// The prop is still accepted (and ignored) so existing callers keep compiling
// until they drop it.
import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { t } from '../lib/i18n';
import { navItems, activeNavId, goNav, type NavId } from '../lib/nav-items';

/** The three destinations that earn a mobile tab. */
const TAB_IDS: readonly NavId[] = ['home', 'workspace', 'pomy'];
const TABS = navItems(TAB_IDS);

export function MobileTabBar(
  // §V2 — `onDark` forces the dark-glass chrome variant for hosts whose
  // canvas is permanently dark regardless of theme (the idle Home hero).
  // A light tab bar over the dark hero split the shell into two visual
  // worlds; the host that owns the dark canvas owns saying so.
  { onDark }: { active?: string; onDark?: boolean } = {}
): JSX.Element {
  const [active, setActive] = useState<NavId | null>(() => activeNavId());
  useEffect(() => {
    const sync = (): void => setActive(activeNavId());
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  return (
    <nav className={`pf-tabbar${onDark ? ' pf-tabbar--ondark' : ''}`} aria-label="Primary (mobile)">
      {TABS.map((tab) => {
        const on = active === tab.id;
        return (
          <button key={tab.id} type="button" className={`pf-tab${on ? ' is-on' : ''}`}
            onClick={() => goNav(tab.hash)} aria-current={on ? 'page' : undefined}>
            <tab.Icon size={21} />
            <span>{t(tab.i18nKey) || tab.fallback}</span>
          </button>
        );
      })}
    </nav>
  );
}
